import { useRef, useState } from "react";
import {
  useAddStandardsSetRelation,
  useCreateStandardsSetDraft,
  useDeleteStandardsSetRelation,
  useFetchStandardsSetRelations,
  useFetchStandardsSets,
  usePublishStandardsSet,
  useUnpublishStandardsSet,
  useUpdateStandardsSetDraft,
} from "@/search/NewSearchPageMVP/services";
import { IStandardsSetListViewModel } from "@/search/NewSearchPageMVP/types/IStandardsSetListViewModel";

export const useStandardsSetAPI = (
  editData: IStandardsSetListViewModel | undefined,
) => {
  const { data: standardsSets } = useFetchStandardsSets({});
  const { data: draftStandardsSetRelation } = useFetchStandardsSetRelations({
    standardSetId: editData?.id || "",
  });
  const { mutateAsync: createDraft } = useCreateStandardsSetDraft();
  const { mutateAsync: publishStandardsSet } = usePublishStandardsSet();
  const { mutateAsync: unpublishStandardsSet } = useUnpublishStandardsSet();
  const { mutateAsync: addStandardsSetRelation } = useAddStandardsSetRelation();
  const { mutateAsync: deleteStandardsSetRelation } =
    useDeleteStandardsSetRelation();
  const { mutateAsync: updateStandardsSet } = useUpdateStandardsSetDraft();

  const [draftStandardsSetId, setDraftStandardsSetId] = useState<string | null>(
    null,
  );
  const operationErrorMessage = useRef("");

  const handleCreateDraftStandardsSet = async () => {
    try {
      if (!draftStandardsSetId) {
        const { id } = await createDraft();
        setDraftStandardsSetId(id);
      }
    } catch (error) {
      console.error("Error creating a draft standards set: ", error);
      operationErrorMessage.current =
        "An error has occurred. Please try again.";
    }
  };

  const handlePublishStandardsSet = async (standardsSetId: string) => {
    try {
      if (standardsSetId) {
        await publishStandardsSet({
          standardsSetId,
        });
        setDraftStandardsSetId(null);
      }
    } catch (error) {
      console.error("Error publishing standards set: ", error);
      operationErrorMessage.current =
        "An error has occurred. Please try again.";
    }
  };

  const handleUnpublishStandardsSet = async (standardsSetId: string) => {
    try {
      await unpublishStandardsSet({ standardsSetId });
    } catch (error) {
      console.error("Error unpublishing standards set: ", error);
      operationErrorMessage.current =
        "An error has occurred. Please try again.";
    }
  };

  const handleDeleteStandardsRelationsFromSet = async (
    standardsSetId: string,
    standardsIdsToRemove: string[],
  ) => {
    try {
      const deletePromises = standardsIdsToRemove
        .map((standardId) => {
          const standardRelation = draftStandardsSetRelation?.find(
            (relation) => relation.target_id === standardId,
          );
          if (standardRelation) {
            return deleteStandardsSetRelation({
              standardsSetId,
              relationId: standardRelation.id,
            });
          } else {
            return null;
          }
        })
        .filter((promise): promise is Promise<any> => promise !== null);

      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error deleting standards relation: ", error);
      operationErrorMessage.current =
        "An error has occurred. Please try again.";
    }
  };

  const handleAddStandardsRelationsToSet = async (
    standardsSetId: string,
    standardsIdsToAdd: string[],
    currentNumberOfStandardsInSet: number,
  ) => {
    try {
      const addPromises = standardsIdsToAdd.map((standardId) =>
        addStandardsSetRelation({
          standardsSetId,
          currentNumberOfStandardsInSet,
          standardId,
        }),
      );
      await Promise.all(addPromises);
    } catch (error) {
      console.error("Error adding standards relation: ", error);
      operationErrorMessage.current =
        "An error has occurred. Please try again.";
    }
  };

  const handleUpdateStandardsSetTitle = async (
    standardsSetId: string,
    title: string,
  ) => {
    try {
      await updateStandardsSet({
        standardsSetId,
        title,
      });
    } catch (error) {
      console.error("Error updating standards set title: ", error);
      operationErrorMessage.current =
        "An error has occurred. Please try again.";
    }
  };

  const resetOperationErrorMessage = () => {
    operationErrorMessage.current = "";
  };

  return {
    draftStandardsSetId,
    handleAddStandardsRelationsToSet,
    handleCreateDraftStandardsSet,
    handleDeleteStandardsRelationsFromSet,
    handlePublishStandardsSet,
    handleUnpublishStandardsSet,
    handleUpdateStandardsSetTitle,
    operationErrorMessage,
    resetOperationErrorMessage,
    setDraftStandardsSetId,
    standardsSets,
  };
};
